import React, { useState } from "react";
import {
  FiArrowRight,
  FiCheck,
  FiCreditCard,
  FiMail,
  FiPhone,
  FiRefreshCw,
  FiShield,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/footer.scss";

function Footer() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const trustIcons = {
    secure: FiShield,
    invoice: FiCreditCard,
    returns: FiRefreshCw,
  };

  const handleNewsletterSubmit = (event) => {
    event.preventDefault();
    setIsSubscribed(true);
    setEmail("");
    setConsent(false);
  };

  return (
    <footer className="site-footer" role="contentinfo">
      <section className="site-footer__trust" aria-label="Deine Vorteile">
        <div className="site-footer__trust-inner">
          {siteConfig.trustBenefits.map((benefit) => {
            const Icon = trustIcons[benefit.id] || FiCheck;
            return (
              <div className="site-footer__trust-item" key={benefit.id}>
                <Icon aria-hidden="true" />
                <div>
                  <strong>{benefit.title}</strong>
                  <span>{benefit.text}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section
        className="site-footer__newsletter"
        aria-labelledby="newsletter-title"
      >
        <div className="site-footer__newsletter-copy">
          <span className="site-footer__eyebrow">
            {siteConfig.newsletter.eyebrow}
          </span>
          <h2 id="newsletter-title">{siteConfig.newsletter.title}</h2>
          <ul>
            {siteConfig.newsletter.benefits.map((benefit) => (
              <li key={benefit}>
                <FiCheck aria-hidden="true" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <form
          className="site-footer__newsletter-form"
          onSubmit={handleNewsletterSubmit}
        >
          <div className="site-footer__newsletter-field">
            <label className="site-footer__sr-only" htmlFor="newsletter-email">
              {siteConfig.newsletter.placeholder}
            </label>
            <FiMail aria-hidden="true" />
            <input
              id="newsletter-email"
              type="email"
              value={email}
              placeholder={siteConfig.newsletter.placeholder}
              autoComplete="email"
              required
              onChange={(event) => {
                setEmail(event.target.value);
                setIsSubscribed(false);
              }}
            />
            <button type="submit">
              <span>{siteConfig.newsletter.buttonLabel}</span>
              <FiArrowRight aria-hidden="true" />
            </button>
          </div>
          <label className="site-footer__consent">
            <input
              type="checkbox"
              checked={consent}
              required
              onChange={(event) => {
                setConsent(event.target.checked);
                setIsSubscribed(false);
              }}
            />
            <span>
              {siteConfig.newsletter.consent}{" "}
              <Link to="/privacy">Mehr erfahren</Link>
            </span>
          </label>
          <p className="site-footer__form-status" aria-live="polite">
            {isSubscribed ? siteConfig.newsletter.success : ""}
          </p>
        </form>
      </section>

      <div className="site-footer__main">
        <div className="site-footer__service">
          <span className="site-footer__eyebrow">
            {siteConfig.footer.serviceEyebrow}
          </span>
          <h2>{siteConfig.contact.heading}</h2>
          <p>{siteConfig.footer.serviceText}</p>

          <a
            className="site-footer__contact-line"
            href={`mailto:${siteConfig.contact.email}`}
          >
            <FiMail aria-hidden="true" />
            <span>{siteConfig.contact.email}</span>
          </a>
          <Link className="site-footer__service-link" to="/contact">
            {siteConfig.footer.contactLinkLabel}
            <FiArrowRight aria-hidden="true" />
          </Link>
          <div
            className="site-footer__social"
            aria-label={siteConfig.footer.socialTitle}
          >
            <span>{siteConfig.footer.socialTitle}</span>
            <div>
              {siteConfig.social.map((network) => (
                <a
                  key={network.label}
                  href={network.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {network.label}
                  <span className="site-footer__sr-only">
                    {" "}
                    (öffnet in einem neuen Fenster)
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="site-footer__links">
          {siteConfig.footer.columns.map((column) => (
            <section key={column.id} aria-labelledby={`footer-${column.id}`}>
              <h3 id={`footer-${column.id}`}>{column.title}</h3>
              <ul>
                {column.links.map((link) => (
                  <li key={`${column.id}-${link.href}`}>
                    <Link to={link.href}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>

      <div className="site-footer__bottom">
        <Link
          className="site-footer__wordmark"
          to="/"
          aria-label={siteConfig.logo.ariaLabel}
        >
          {siteConfig.logo.src ? (
            <img src={siteConfig.logo.src} alt={siteConfig.logo.alt} />
          ) : (
            siteConfig.logo.text
          )}
        </Link>
        <div
          className="site-footer__payments"
          aria-label={siteConfig.footer.paymentTitle}
        >
          <span>{siteConfig.footer.paymentTitle}</span>
          <ul>
            {siteConfig.footer.paymentMethods.map((method) => (
              <li key={method}>{method}</li>
            ))}
          </ul>
        </div>
        <div className="site-footer__legal">
          <span>{siteConfig.footer.locale}</span>
          <span>
            © {new Date().getFullYear()} {siteConfig.footer.copyrightName}
          </span>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
